import { useRef, useState } from 'react';
import { breakpoints, RadioGroup, Stack, ValidationMessage } from '@etg/wings';
import { useTranslation, useValidation } from '@eti/providers';
import { constants } from '@eti/utils';
import { css } from '@eti/styles';
import { Field, FormSection } from 'redux-form';
import { useMediaQuery } from 'usehooks-ts';
import { screenReaderOnly } from '../../../common/utils/styles';
import useBaggageTracker from '../hooks/useBaggageTracker';
import { usePersuasionModal } from '../../../common/context/PersuasionModalContext';
import { isEnabled } from '../../../common/utils/modal';
import ProductModal from '../../../common/components/ProductModal';
import { BAGGAGE_VALUES } from '../utils/baggageUtils';
import BaggageSelectionRadioBoxLabel from './BaggageSelectionRadioBoxLabel';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const errorStyles = css`
  margin-inline-start: 0;
`;
const BaggageSelectionRadioGroup = _ref => {
  let {
    baggageOptions,
    input,
    meta,
    onChange,
    order,
    persuasionContent,
    persuasionTitle,
    productName,
    productPosition,
    radioGroupScreenReaderDescription,
    rootElement,
    shouldApplySelectionToAll,
    travelerId,
    totalBaggageQuantity,
    secondBagProductId
  } = _ref;
  const {
    t
  } = useTranslation();
  const [selectedBaggageOption, setSelectedBaggageOption] = useState('');
  const isDesktop = useMediaQuery(`(min-width: ${breakpoints._768})`);
  const yesOptionRef = useRef(null);
  const setYesOptionRef = el => {
    yesOptionRef.current = el;
  };
  const {
    isOpen,
    onConfirm,
    onDecline,
    setIsOpen,
    setOnConfirm,
    setOnDecline
  } = usePersuasionModal();
  const hasError = Boolean((meta === null || meta === void 0 ? void 0 : meta.touched) && (meta === null || meta === void 0 ? void 0 : meta.error));
  const selectedTraveler = order.travelers[Number(travelerId)];
  const travelersWithoutInfants = order.travelers.filter(_ref2 => {
    let {
      ageType
    } = _ref2;
    return ageType !== constants.ageTypes.INFANT;
  });
  const {
    addToCartEventOnChange,
    addToCartModalEvent
  } = useBaggageTracker(productName, travelersWithoutInfants.length, totalBaggageQuantity, travelerId, productPosition, shouldApplySelectionToAll, rootElement);
  const handleChange = (selectedTravelerId, selectedValue) => {
    var _order$persuasion;
    if (!selectedValue) {
      return;
    }
    const isFirstTraveler = Number(selectedTravelerId) === 0;
    const showPersuasion = isFirstTraveler && (order === null || order === void 0 || (_order$persuasion = order.persuasion) === null || _order$persuasion === void 0 ? void 0 : _order$persuasion.isEnabled);
    if (showPersuasion && selectedValue === 'false') {
      onChange(BAGGAGE_VALUES.FALSE, selectedTravelerId, shouldApplySelectionToAll);
      setIsOpen(true);
      setOnConfirm(() => () => {
        const value = selectedBaggageOption || baggageOptions[1].value;
        setIsOpen(false);
        setTimeout(() => {
          var _yesOptionRef$current;
          onChange(value, selectedTravelerId, shouldApplySelectionToAll);
          yesOptionRef === null || yesOptionRef === void 0 || (_yesOptionRef$current = yesOptionRef.current) === null || _yesOptionRef$current === void 0 || _yesOptionRef$current.focus();
        }, 530);
      });
      setOnDecline(() => () => {
        onChange(BAGGAGE_VALUES.FALSE, selectedTravelerId, shouldApplySelectionToAll);
        setIsOpen(false);
      });
    } else {
      onChange(selectedValue, selectedTravelerId, shouldApplySelectionToAll);
    }
    setSelectedBaggageOption(selectedValue);
    addToCartEventOnChange(selectedValue);
  };
  const handleOnConfirm = () => {
    onConfirm();
    addToCartModalEvent(true);
  };
  const handleOnDecline = () => {
    onDecline();
    addToCartModalEvent(false);
  };
  return /*#__PURE__*/_jsxs("div", {
    ref: rootElement,
    children: [isEnabled(order === null || order === void 0 ? void 0 : order.persuasion) && /*#__PURE__*/_jsx(ProductModal, {
      content: persuasionContent,
      isOpen: isOpen,
      onConfirm: handleOnConfirm,
      onDecline: handleOnDecline,
      productName: productName,
      title: persuasionTitle
    }), /*#__PURE__*/_jsxs(Stack, {
      spacing: 8,
      children: [hasError && /*#__PURE__*/_jsx(ValidationMessage, {
        className: errorStyles,
        "data-testid": "purchasableBaggage-select-option-error-message",
        variant: "error",
        children: t('Product.SelectOneValidation.Message')
      }), /*#__PURE__*/_jsxs(RadioGroup, {
        "data-testid": `${productName}-radiogroup-${travelerId}`,
        hasError: hasError,
        name: `${productName}-traveler-${travelerId}`,
        onChange: event => handleChange(String(selectedTraveler.id), event === null || event === void 0 ? void 0 : event.target.value),
        selectedValue: input === null || input === void 0 ? void 0 : input.value,
        children: [radioGroupScreenReaderDescription && /*#__PURE__*/_jsx(RadioGroup.Legend, {
          className: screenReaderOnly,
          children: radioGroupScreenReaderDescription
        }), /*#__PURE__*/_jsx(Stack, {
          "data-offer-rate": order.id,
          spacing: isDesktop ? 8 : 16,
          children: baggageOptions === null || baggageOptions === void 0 ? void 0 : baggageOptions.map(option => /*#__PURE__*/_jsx(BaggageSelectionRadioBoxLabel, {
            baggageOption: option,
            input: input,
            order: order,
            productName: productName,
            secondBagProductId: secondBagProductId,
            setYesOptionRef: setYesOptionRef
          }, `${productName}-traveler-${travelerId}-${option.id}`))
        })]
      })]
    })]
  });
};
const BaggageSelection = _ref3 => {
  let {
    baggageOptions,
    onChange,
    order,
    persuasionContent,
    persuasionTitle,
    productName,
    radioGroupScreenReaderDescription,
    rootElement,
    productPosition,
    secondBagProductId,
    shouldApplySelectionToAll,
    travelerId,
    totalBaggageQuantity
  } = _ref3;
  const validate = useValidation(productName);
  const name = `${productName}-traveler-${travelerId}`;
  return /*#__PURE__*/_jsx(FormSection, {
    name: productName,
    children: /*#__PURE__*/_jsx(Field, {
      component: BaggageSelectionRadioGroup,
      format: reduxValue => (reduxValue === null || reduxValue === void 0 ? void 0 : reduxValue.value) || undefined,
      name: name,
      props: {
        baggageOptions,
        onChange,
        order,
        persuasionContent,
        persuasionTitle,
        productName,
        productPosition,
        radioGroupScreenReaderDescription,
        rootElement,
        travelerId,
        secondBagProductId,
        shouldApplySelectionToAll,
        totalBaggageQuantity
      },
      validate: validate
    })
  });
};
export default BaggageSelection;
//# sourceMappingURL=BaggageSelection.js.map